<script setup lang="ts">
import { computed, inject, onMounted, ref, useTemplateRef } from "vue";
import { useRoute, useRouter } from "vue-router";

import type { BreadcrumbItem } from "@/components/ui/BreadcrumbNavigation.vue";
import TabPanel from "@/components/ui/TabPanel.vue";
import TabPanelContent from "@/components/ui/TabPanelContent.vue";
import { ReportType } from "@/dto";
import { raiseErrorKey } from "@/injectionKeys";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { isValidProjectDetailSection, ProjectDetailSections, RouteNames } from "@/router";
import { useRemainingHeight } from "@/services/remainingHeight";
import { useWorkspaceFetcher } from "@/services/workspaceFetcher";
import ProjectDetailViewComparisonList from "@/views/projects/ProjectDetailViewComparisonList.vue";
import ProjectDetailViewExperimentsSearch from "@/views/projects/ProjectDetailViewExperimentsSearch.vue";

const route = useRoute();
const router = useRouter();

const raiseError = inject(raiseErrorKey);

const { remainingHeight, computeRemainingHeight } = useRemainingHeight(".tabs");

const tabs = useTemplateRef<InstanceType<typeof TabPanel>>("tabs");
const activeTab = ref<string | null>(null);

const workspacePublicId = computed(() => route.params.workspacePublicId as string);
const projectName = computed(() => route.params.projectName as string);

const workspace = useWorkspaceFetcher(workspacePublicId.value);

const breadcrumbItems = computed(() => {
  const items: BreadcrumbItem[] = [
    {
      label: workspace.value?.name ?? workspacePublicId.value,
      to: { name: RouteNames.Projects, params: { workspacePublicId: workspacePublicId.value } },
      icon: "icon-masonry",
    },
    {
      label: projectName.value,
      icon: "icon-punch-card",
      iconColor: "var(--color-purple-500)",
    },
  ];

  if (activeTab.value) {
    items.push({
      label: capitalize(activeTab?.value),
    });
  }

  return items;
});

function capitalize(s: string) {
  return s[0]!.toUpperCase() + s.substring(1);
}

function onTabChange(tabName: string) {
  activeTab.value = tabName;
  router.push({
    name: RouteNames.ProjectDetail,
    params: {
      workspacePublicId: workspacePublicId.value,
      projectName: projectName.value,
      section: tabName,
      itemId: route.params.itemId,
    },
  });
}

onMounted(() => {
  if (tabs.value) {
    const routeSection = route.params.section as string | undefined;
    if (routeSection && !isValidProjectDetailSection(routeSection)) {
      raiseError?.(404);
      return;
    }
    const section =
      routeSection && routeSection.length > 0
        ? routeSection
        : ProjectDetailSections.EstimatorReports;
    tabs.value.selectTab(section);
  }
});
</script>

<template>
  <DefaultPageLayout :breadcrumb-items="breadcrumbItems">
    <div class="experiment-view">
      <TabPanel
        ref="tabs"
        :style="{ '--content-height': `${Math.max(remainingHeight, 550)}px` }"
        :uses-anchor="false"
        @tab-change="onTabChange"
        @vue:mounted="computeRemainingHeight"
      >
        <TabPanelContent :name="ProjectDetailSections.EstimatorReports" icon="icon-cube">
          <ProjectDetailViewExperimentsSearch :report-type="ReportType.EstimatorReport" />
        </TabPanelContent>

        <TabPanelContent
          :name="ProjectDetailSections.CrossValidationReports"
          icon="icon-playground"
        >
          <ProjectDetailViewExperimentsSearch :report-type="ReportType.CrossValidationReport" />
        </TabPanelContent>

        <TabPanelContent :name="ProjectDetailSections.Comparisons" icon="icon-scale">
          <ProjectDetailViewComparisonList />
        </TabPanelContent>
      </TabPanel>
    </div>
  </DefaultPageLayout>
</template>

<style scoped>
.experiment-view {
  :deep(.tab-selector) {
    margin: var(--numbers-24) 0;
  }

  :deep(.tab-content) {
    height: calc(var(--content-height) - var(--numbers-20));
  }
}
</style>
